import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { CheckCircle2, MessageSquare } from 'lucide-react';
import { useBrainStore } from '@/state/brainStore';
import { useUIStore } from '@/state/uiStore';
import { AGENTS } from '@/data/agents';
import { closeOffMissedLink } from '@/domain/unpaidLinkFlow';
import { messageAboutOpsItem } from '@/domain/opsThreadLoop';
import { linkSenderLabel, SENDER_ROLE_WORD } from '@/domain/depositStatus';
import { missedLinkStateLabel, type MissedLink } from '@/domain/missedLinks';
import type { LinkSenderRole, MissedLinkCloseOutcome } from '@/domain/types';
import { aud, simDateShort, simTimeFull } from '@/utils/format';
import OpsThreadPill from '@/components/ops/OpsThreadPill';
import ResendLinkAction from '@/components/deposits/ResendLinkAction';

export const CLOSE_OFF_OUTCOMES: ReadonlyArray<{ key: MissedLinkCloseOutcome; label: string }> = [
  { key: 'paid-another-way', label: 'Paid another way' },
  { key: 'client-declined', label: 'Client declined' },
  { key: 'rebooked-other-slot', label: 'Rebooked other slot' },
  { key: 'other', label: 'Other' },
];

/** The role words, shared with the ledger (one map, tv-7n5f). */
export const SENDER_ROLE_LABEL: Record<LinkSenderRole, string> = SENDER_ROLE_WORD;

/**
 * An unpaid deposit link to close off (W5 mg, board 08): the link facts, a
 * "Message <sender>" DM tagged to the row (the reply loop), and the close-off
 * (one outcome + a mandatory note). Shared by the Ops "Links >48h" modal and the
 * Sweeper's missed-deposits list (`showMessage` off: the Sweeper sent it).
 */
export default function UnclosedLinkBody({
  link,
  customerName,
  artistName,
  by,
  fromPersonId,
  nowIso,
  showMessage = true,
  resendAs,
  onDone,
}: {
  link: MissedLink;
  customerName: string;
  artistName?: string;
  /** Who is closing it off (the stamp on the deposit). */
  by: string;
  /** The comms person authoring a DM (the signed-in seat). */
  fromPersonId?: string | null;
  nowIso: string;
  showMessage?: boolean;
  /**
   * W5b #2/Q3: whose follow-up a resend from here becomes. Default: the Sweeper
   * on its own missed-deposits list (showMessage off); on the Ops desk a resent
   * Messenger link stays with Ops (there is no separate Ops link role).
   */
  resendAs?: LinkSenderRole;
  onDone: () => void;
}) {
  const agents = useBrainStore((s) => s.agents);
  const artists = useBrainStore((s) => s.artists);
  const dmThreads = useBrainStore((s) => s.internalDmThreads);
  const pushNotification = useUIStore((s) => s.pushNotification);
  const deposit = useBrainStore((s) => s.deposits[link.depositId]);
  const resendRole: LinkSenderRole | undefined =
    resendAs ?? (!showMessage ? 'sweeper' : link.sentByRole === 'messenger' ? 'messenger' : undefined);

  const [outcome, setOutcome] = useState<MissedLinkCloseOutcome | null>(null);
  const [note, setNote] = useState('');
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const roleLabel = SENDER_ROLE_LABEL[link.sentByRole];
  const senderName = linkSenderLabel(link.sentBy, link.sentByRole);
  // Only a seat backed by a staff/artist record has a Comms inbox to reply from.
  const senderPerson = useMemo(() => {
    if (!link.sentBy) return undefined;
    const agent = agents[link.sentBy] ?? AGENTS[link.sentBy];
    if (agent) return { id: agent.id, firstName: agent.firstName };
    const artist = artists[link.sentBy];
    return artist ? { id: artist.id, firstName: artist.firstName } : undefined;
  }, [agents, artists, link.sentBy]);

  const slotLabel = link.sessionIso ? simTimeFull(link.sessionIso) : 'No slot pinned';
  const template = `Hi ${senderPerson?.firstName ?? 'there'} - ${customerName}'s ${aud(link.amountAud)} deposit link${
    link.sessionIso ? ` for ${simTimeFull(link.sessionIso)}` : ''
  } is still unpaid ${link.ageH}h after it went out. Any word from them? Can you close it out or let me know where it's at?`;
  const body = draft || template;

  const canMessage = showMessage && !!senderPerson && !!fromPersonId && senderPerson.id !== fromPersonId;
  const canClose = !!outcome && note.trim().length > 0;

  const send = () => {
    if (!canMessage || !senderPerson || !fromPersonId) return;
    messageAboutOpsItem({ itemId: link.id, fromPersonId, toPersonId: senderPerson.id, body, at: nowIso });
    pushNotification({ intensity: 'info', title: 'Message sent', body: `${senderName} will reply in the thread.`, ttlMs: 4000 });
  };

  const closeOff = () => {
    if (!outcome || !note.trim()) return;
    const res = closeOffMissedLink({ depositId: link.depositId, outcome, note, by, at: nowIso });
    if (!res.ok) {
      setError(
        res.error === 'already-closed'
          ? 'This link is already closed (paid, re-sent or rebooked).'
          : res.error === 'no-deposit'
            ? "Couldn't find this deposit link - reload and try again."
            : 'Add a note to close it off.',
      );
      return;
    }
    pushNotification({ intensity: 'info', title: 'Link closed off', body: `${customerName} · ${outcomeLabel(outcome)}`, ttlMs: 4000 });
    onDone();
  };

  return (
    <div className="flex flex-col gap-4" data-testid="ucl-body">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest bg-blood/15 text-blood">
          {link.ageH}h unclosed
        </span>
        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest bg-cream-soft/15 text-cream-soft">
          {missedLinkStateLabel(link)}
        </span>
      </div>

      {deposit ? (
        <div className="flex items-center gap-2" data-testid="ucl-resend">
          <ResendLinkAction
            deposit={deposit}
            {...(resendRole ? { sentByRole: resendRole } : {})}
            testId={`ucl-resend-${link.depositId}`}
            onResent={onDone}
          />
        </div>
      ) : null}

      <div className="panel-dark p-3.5 grid grid-cols-2 gap-x-4 gap-y-3" data-testid="ucl-summary">
        <Field k="Slot" v={`${slotLabel}${artistName ? ` · ${artistName}` : ''}`} />
        <Field k="Deposit" v={`${aud(link.amountAud)} · link sent ${simDateShort(link.sentAt)}`} />
        <Field k="Sent by" v={`${senderName} (${roleLabel})`} />
        <div className="min-w-0">
          <p className="label-small-caps">Thread</p>
          <div className="mt-1">
            <OpsThreadPill itemId={link.id} dmThreads={dmThreads} nowIso={nowIso} showNotMessaged />
          </div>
        </div>
      </div>

      {showMessage ? (
        canMessage ? (
          <div className="flex flex-col gap-2">
            <label className="block">
              <span className="label-small-caps">Message to {senderName}</span>
              <textarea
                value={body}
                onChange={(e) => setDraft(e.target.value)}
                rows={3}
                className="mt-2 w-full bg-ink-black/40 border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream focus:outline-none focus:border-gold-bright resize-none"
                data-testid="ucl-message-draft"
              />
            </label>
            <button type="button" onClick={send} className="btn-soft self-start" data-testid="ucl-message">
              <MessageSquare size={13} /> Message {senderPerson!.firstName}
            </button>
          </div>
        ) : (
          <p className="font-serif text-xs text-cream-soft" data-testid="ucl-no-inbox">
            {senderName} ({roleLabel}) has no Comms inbox to reply from. Follow up on the client&rsquo;s own
            thread from their record, then close it off below.
          </p>
        )
      ) : null}

      <div className="border-t border-gold-dim/20 pt-3 flex flex-col gap-3">
        <p className="label-small-caps">Close off · outcome</p>
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Close-off outcome">
          {CLOSE_OFF_OUTCOMES.map((o) => (
            <button
              key={o.key}
              type="button"
              role="radio"
              aria-checked={outcome === o.key}
              onClick={() => setOutcome(o.key)}
              data-testid={`ucl-outcome-${o.key}`}
              className={clsx(
                'rounded-full border px-3 py-1.5 text-xs font-inter transition',
                outcome === o.key
                  ? 'border-gold-bright bg-gold-bright/15 text-gold-bright'
                  : 'border-gold-dim/40 text-cream-soft hover:border-gold-bright/50',
              )}
            >
              {o.label}
            </button>
          ))}
        </div>
        <label className="block">
          <span className="label-small-caps">Note (required)</span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder="What happened, e.g. said she'll come back after payday."
            className="mt-2 w-full bg-ink-black/40 border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream placeholder:text-cream-faint focus:outline-none focus:border-gold-bright resize-none"
            data-testid="ucl-note"
          />
        </label>
        {error ? (
          <p className="font-inter text-xs text-blood" role="alert" data-testid="ucl-error">
            {error}
          </p>
        ) : null}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={closeOff}
            disabled={!canClose}
            className="inline-flex items-center gap-1.5 rounded-md border border-mint/50 bg-mint/10 px-3 py-1.5 text-xs text-mint hover:bg-mint/20 transition disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="ucl-close-off"
          >
            <CheckCircle2 size={13} /> Close off
          </button>
        </div>
      </div>
    </div>
  );
}

function outcomeLabel(o: MissedLinkCloseOutcome): string {
  return CLOSE_OFF_OUTCOMES.find((x) => x.key === o)?.label ?? o;
}

function Field({ k, v }: { k: string; v: string }) {
  return (
    <div className="min-w-0">
      <p className="label-small-caps">{k}</p>
      <p className="font-serif text-sm mt-0.5 text-cream">{v}</p>
    </div>
  );
}
